import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'

// ── Types ───────────────────────────────────────────────────────

export interface MaintenanceCategory {
  id: string
  name: string
  description: string
  sort_order: number
}

export interface MaintenanceType {
  id: string
  category_id: string | null
  name: string
  description: string
}

export interface ServiceOrder {
  id: string
  device_id: string
  schedule_id: string | null
  category_id: string | null
  type_id: string | null
  title: string
  description: string
  status: string
  priority: string
  service_type: string
  odometer_km: number | null
  cost: number | null
  vendor_name: string
  vendor_contact: string
  performed_by: string | null
  performed_at: string | null
  notes: string
  opened_at: string
  started_at: string | null
  completed_at: string | null
  closed_at: string | null
}

export interface ServiceDocument {
  id: string
  service_order_id: string
  filename: string
  original_name: string
  file_size: number
  mime_type: string
  doc_type: string
  created_at: string
}

export interface ServiceIssue {
  id: string
  device_id: string
  service_order_id: string | null
  title: string
  description: string
  severity: string
  status: string
  reported_by: string | null
  resolved_at: string | null
  created_at: string
}

// ── Hooks ───────────────────────────────────────────────────────

function unwrapData<T>(d: unknown): T[] {
  if (Array.isArray(d)) return d
  const o = d as { data?: T[] } | undefined
  return o?.data ?? []
}

// Categories
export function useCategories() {
  return useQuery({
    queryKey: ['service-categories'],
    queryFn: () => api.get('/service/categories').then((r) => unwrapData<MaintenanceCategory>(r.data)),
    retry: 1,
  })
}

export function useCreateCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string; description?: string }) =>
      api.post('/service/categories', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['service-categories'] }),
  })
}

export function useDeleteCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/service/categories/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['service-categories'] }),
  })
}

// Types
export function useMaintenanceTypes() {
  return useQuery({
    queryKey: ['service-types'],
    queryFn: () => api.get('/service/types').then((r) => unwrapData<MaintenanceType>(r.data)),
    retry: 1,
  })
}

export function useCreateMaintenanceType() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { category_id?: string; name: string; description?: string }) =>
      api.post('/service/types', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['service-types'] }),
  })
}

// Service Orders
export function useServiceOrders(status?: string) {
  return useQuery({
    queryKey: ['service-orders', status],
    queryFn: () =>
      api.get('/service/orders', { params: status ? { status } : undefined })
        .then((r) => unwrapData<ServiceOrder>(r.data)),
  })
}

export function useServiceOrder(id: string) {
  return useQuery({
    queryKey: ['service-order', id],
    queryFn: () => api.get<{ order: ServiceOrder; documents: ServiceDocument[]; issues: ServiceIssue[] }>(`/service/orders/${id}`).then((r) => r.data),
    enabled: !!id,
  })
}

export function useCreateServiceOrder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<ServiceOrder>) =>
      api.post('/service/orders', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['service-orders'] }),
  })
}

export function useUpdateServiceOrder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: Partial<ServiceOrder> & { id: string }) =>
      api.put(`/service/orders/${id}`, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['service-orders'] })
      qc.invalidateQueries({ queryKey: ['service-order'] })
    },
  })
}

export function useUpdateServiceOrderStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api.put(`/service/orders/${id}/status`, { status }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['service-orders'] }),
  })
}

// Documents
export function useUploadDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ orderId, file, docType }: { orderId: string; file: File; docType?: string }) => {
      const formData = new FormData()
      formData.append('file', file)
      if (docType) formData.append('doc_type', docType)
      return api.post(`/service/orders/${orderId}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }).then((r) => r.data)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['service-order'] }),
  })
}

export function useDeleteDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ orderId, docId }: { orderId: string; docId: string }) =>
      api.delete(`/service/orders/${orderId}/documents/${docId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['service-order'] }),
  })
}

// Issues
export function useServiceIssues(deviceId?: string) {
  return useQuery({
    queryKey: ['service-issues', deviceId],
    queryFn: () =>
      api.get('/service/issues', { params: deviceId ? { device_id: deviceId } : undefined })
        .then((r) => unwrapData<ServiceIssue>(r.data)),
  })
}

export function useCreateIssue() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { device_id: string; title: string; description?: string; severity?: string }) =>
      api.post('/service/issues', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['service-issues'] }),
  })
}

export function useResolveIssue() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, serviceOrderId }: { id: string; serviceOrderId?: string }) =>
      api.put(`/service/issues/${id}/resolve`, { service_order_id: serviceOrderId }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['service-issues'] }),
  })
}
